import React, { useMemo } from 'react';
import { specifyStatus } from '../../../../../utilities/generalMappings';
import { Status } from '../../../../../types/redux/data/dataTypes';
import { isNavReconPipelineStageSucceeded } from '../pipelineMetadata';

interface NavReconOverviewStatsProps {
  title: string;
  rows: any[];
  showKpis?: boolean;
}

const NavReconOverviewStats: React.FC<NavReconOverviewStatsProps> = ({
  title,
  rows,
  showKpis = true,
}) => {
  const totalFunds = rows.length;

  // Number of funds where "NAV Reconciliation Calculation" latest snapshot succeeded
  const completeFundsCount = useMemo(() => {
    return rows.filter((r) => {
      const stage = r.pipelineFundMetadata?.stages?.find(
        (s: any) => s.rs_data_name === 'nav_reconciliation_risk_monitor'
      );
      return isNavReconPipelineStageSucceeded(stage?.snapshots);
    }).length;
  }, [rows]);

  // Helper to determine if a cell status is Alert or Fail
  const isFailOrAlert = (val: string | null | undefined) => {
    if (!val) return false;
    const status = specifyStatus(val);
    return status === Status.Fail || status === Status.Alert;
  };

  // Number of funds requiring action (has either a fail or alert in any status column, and is not manually approved)
  const actionRequiredCount = useMemo(() => {
    return rows.filter((r) => {
      if (!r.overviewHasPayload) return false;
      if (r.auditStatus === 'approved') return false;

      const statusFields = [
        r.share_class_price_deviation_status,
        r.est_return_status,
        r.unit_return_status,
        r.alertStatus_six_caceis_returns_status,
        r.alertStatus_caceis_share_price_returns_status,
        r.alertStatus_six_share_price_returns_status,
        r.alertStatus_sc_nav_px_returns_status,
        r.alertStatus_sc_unit_status,
      ];

      return statusFields.some(isFailOrAlert);
    }).length;
  }, [rows]);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 w-full">
      {/* Table Title */}
      <h2 className="text-base font-semibold text-mainblue whitespace-nowrap">
        {title}
      </h2>

      {showKpis ? (
        <div className="flex flex-wrap items-center gap-3 text-xs">
          {/* Calculations Complete */}
        <div className="flex items-center space-x-2 rounded-lg border border-emerald-100 bg-emerald-50/40 px-3 py-1.5 shadow-sm">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="font-medium text-emerald-700">NAV Calculation Complete:</span>
          <span className="font-bold text-emerald-800">
            {completeFundsCount} <span className="text-emerald-500 font-normal">/ {totalFunds}</span>
          </span>
        </div>

        {/* Requires Audit */}
        <div className={`flex items-center space-x-2 rounded-lg border px-3 py-1.5 shadow-sm transition-all duration-300 ${
          actionRequiredCount > 0
            ? 'border-red-100 bg-red-50/40 text-red-700'
            : 'border-emerald-100 bg-emerald-50/40 text-emerald-700'
        }`}>
          <span className={`h-2 w-2 rounded-full ${actionRequiredCount > 0 ? 'bg-red-500' : 'bg-emerald-500'}`} />
          <span className="font-medium">Requires Audit:</span>
          <span className="font-bold">
            {actionRequiredCount} <span className={actionRequiredCount > 0 ? 'text-red-500 font-normal' : 'text-emerald-500 font-normal'}>/ {completeFundsCount}</span>
          </span>
        </div>
        </div>
      ) : null}
    </div>
  );
};

export default NavReconOverviewStats;
